import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Which tickets arrived on the Dashboard while it was open (16 Oct), so a
 * block a live event brought in is marked for a moment. Whatever shows in
 * the first `SETTLE_MS` is the page loading, not news; after that, a ticket
 * the page has not shown before is new, once.
 */
const SETTLE_MS = 2000;
/** How long a new block stays marked. */
export const ARRIVAL_MS = 2400;

type Arrivals = { opened: number; known: Set<string> };
const ArrivalsContext = createContext<Arrivals | null>(null);

export function ArrivalsProvider({ children }: { children: ReactNode }) {
  const arrivals = useRef<Arrivals>({ opened: Date.now(), known: new Set() });
  return <ArrivalsContext.Provider value={arrivals.current}>{children}</ArrivalsContext.Provider>;
}

/** True for a moment when this ticket first shows after the page settled. */
export function useArrived(ticketId: string): boolean {
  const arrivals = useContext(ArrivalsContext);
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    if (!arrivals || arrivals.known.has(ticketId)) return;
    arrivals.known.add(ticketId);
    if (Date.now() - arrivals.opened >= SETTLE_MS) setArrived(true);
  }, [arrivals, ticketId]);
  // Its own effect: React runs an effect, cleans it up and runs it again in development, and a timer set
  // where the ticket is first seen was cleared by that cleanup and never set again, so the mark stayed.
  useEffect(() => {
    if (!arrived) return undefined;
    const timer = setTimeout(() => setArrived(false), ARRIVAL_MS);
    return () => clearTimeout(timer);
  }, [arrived]);
  return arrived;
}
